/**
 * R3 UI close-out — Internal Preview modal.
 *
 * Distinct from the external `/v/:token` viewer player:
 *   - Native HTML5 controls (including the browser-built-in download menu)
 *   - No watermark, no heartbeat, no anti-redistribution headers
 *   - Streams via GET /api/runs/{run_id}/internal-video with HTTP Range
 *
 * Gated by `workflow.run` permission (presenter+).  Viewers do not see
 * the button that opens this modal.
 */
import React, { useEffect, useState } from "react";
import { Loader2, X, Film, AlertTriangle, Download } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, API_BASE, formatApiError } from "@/lib/api";

function fmtBytes(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function fmtDuration(s) {
  if (s == null) return "—";
  const m = Math.floor(s / 60);
  const sec = Math.round(s - m * 60);
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
}

export default function InternalVideoPreviewModal({
  open,
  onOpenChange,
  runId,
  runNumberLabel,
  workflowName,
}) {
  const [meta, setMeta] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cost, setCost] = useState(null);

  useEffect(() => {
    if (!open || !runId) return undefined;
    let cancelled = false;
    let createdBlobUrl = null;
    setError(null);
    setMeta(null);
    setBlobUrl(null);
    setCost(null);
    setLoading(true);

    (async () => {
      try {
        const metaResp = await api.get(`/runs/${runId}/internal-video/meta`);
        if (cancelled) return;
        setMeta(metaResp.data);
        // Cost is non-blocking — fetched in parallel; failure just hides it.
        api
          .get(`/runs/${runId}/cost`)
          .then((r) => {
            if (!cancelled) setCost(r.data);
          })
          .catch(() => {});
        const videoResp = await api.get(`/runs/${runId}/internal-video`, {
          responseType: "blob",
        });
        if (cancelled) return;
        createdBlobUrl = window.URL.createObjectURL(videoResp.data);
        setBlobUrl(createdBlobUrl);
      } catch (err) {
        if (!cancelled) {
          setError(formatApiError(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (createdBlobUrl) window.URL.revokeObjectURL(createdBlobUrl);
    };
  }, [open, runId]);

  // Revoke when modal closes externally.
  useEffect(() => {
    if (open) return undefined;
    return () => {
      if (blobUrl) window.URL.revokeObjectURL(blobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function handleDownloadFallback() {
    if (!blobUrl || !meta?.filename) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = meta.filename;
    a.click();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[960px] gap-3 p-0 overflow-hidden"
        data-testid="internal-video-preview-modal"
      >
        <header
          className="flex items-start justify-between gap-3 border-b border-slate-200 bg-slate-50 px-5 py-3"
          data-testid="internal-video-preview-header"
        >
          <div className="min-w-0 flex-1">
            <DialogTitle className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <Film size={15} className="text-indigo-600" />
              Internal Preview
              <span
                className="font-mono text-xs font-medium text-indigo-700"
                data-testid="internal-video-preview-run-number"
              >
                {runNumberLabel || ""}
              </span>
            </DialogTitle>
            <p
              className="mt-0.5 truncate text-xs text-slate-500"
              data-testid="internal-video-preview-subline"
            >
              {workflowName || "—"}
              {meta && (
                <>
                  {" · "}
                  {fmtDuration(meta.final_duration_s)}
                  {" · "}
                  {fmtBytes(meta.size_bytes)}
                </>
              )}
              {cost?.total_usd != null && (
                <>
                  {" · "}
                  <span
                    className="font-mono text-emerald-700"
                    data-testid="internal-video-preview-cost"
                  >
                    {`$${Number(cost.total_usd).toFixed(4)}`}
                  </span>
                </>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-full p-1 text-slate-500 hover:bg-slate-200 hover:text-slate-900"
            aria-label="Close preview"
            data-testid="internal-video-preview-close"
          >
            <X size={16} />
          </button>
        </header>

        <div className="px-5 pb-5">
          {error ? (
            <div
              className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800"
              data-testid="internal-video-preview-error"
            >
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          ) : loading ? (
            <div
              className="flex aspect-video w-full items-center justify-center rounded-lg bg-slate-100 text-sm text-slate-500"
              data-testid="internal-video-preview-loading"
            >
              <Loader2 size={16} className="mr-2 animate-spin" />
              Loading video…
            </div>
          ) : blobUrl ? (
            <video
              controls
              controlsList="nodownload-disabled"
              src={blobUrl}
              preload="metadata"
              className="aspect-video w-full rounded-lg border border-slate-200 bg-black"
              data-testid="internal-video-preview-player"
            >
              Your browser doesn&apos;t support HTML5 video.
            </video>
          ) : null}

          {meta && blobUrl ? (
            <div
              className="mt-3 flex items-center justify-between gap-2 text-[11px] text-slate-500"
              data-testid="internal-video-preview-meta"
            >
              <span className="truncate font-mono">{meta.filename}</span>
              <button
                type="button"
                onClick={handleDownloadFallback}
                className="inline-flex items-center gap-1 rounded-md border border-indigo-200 px-2 py-1 text-[11px] font-medium text-indigo-700 hover:bg-indigo-50"
                data-testid="internal-video-preview-download-fallback"
              >
                <Download size={11} />
                Download MP4
              </button>
            </div>
          ) : null}

          {/* Hidden direct link helps screen-readers + lets us prove the
              endpoint is wired in DOM-based tests. */}
          {meta ? (
            <a
              href={`${API_BASE}/runs/${runId}/internal-video`}
              className="sr-only"
              data-testid="internal-video-preview-endpoint"
            >
              {`${API_BASE}/runs/${runId}/internal-video`}
            </a>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
